import {
    pureLgMin,
    pureMdMax,
    pureMdMin,
    pureSmMax,
    pureSmMin,
    pureXsMax,
} from "@khan/shared-styles";

import type {Order, Sort} from "./types.ts";

/**
 * Given an object and a key, return the value at that key.
 *
 * This is specially helpful when the key is a nested object, e.g.
 * "foo.bar.baz".
 *
 * @param {Object} obj - The object to get the value from.
 * @param {string} key - The key/accessor to get the value from.
 * @returns {*} The value at the given key.
 */
export function getValueFromKey(obj: any, key: string): any {
    const keys = key.split(".");
    return keys.reduce((acc, key) => {
        return acc[key];
    }, obj);
}

/**
 * Paginate an array of items (if a page size is provided).
 *
 * @param {Array<any>} items - The items to paginate.
 * @param {number} page - The page number to get.
 * @param {number} pageSize - The number of items per page.
 * @returns {Array<any>} The items for the given page.
 */
export function getDataByPage<T>(
    items: Array<T>,
    page: number,
    pageSize?: number,
): Array<T> {
    if (!pageSize || pageSize <= 0) {
        return items;
    }

    const start = (page - 1) * pageSize;
    const end = page * pageSize;
    return items.slice(start, end);
}

/**
 * Given two objects and a key, compare the values at that key.
 *
 * @param {Object} a - The first object to compare.
 * @param {Object} b - The second object to compare.
 * @param {string} orderBy - The key/accessor to compare.
 * @returns {number} The comparison result that can be used to sort.
 */
function descendingComparator<T>(a: T, b: T, orderBy: string): number {
    const aSortKey = getValueFromKey(a, orderBy);
    const bSortKey = getValueFromKey(b, orderBy);

    if (aSortKey == null && bSortKey == null) {
        return 0;
    }
    if (aSortKey == null) {
        return 1;
    }
    if (bSortKey == null) {
        return -1;
    }

    if (bSortKey < aSortKey) {
        return -1;
    }
    if (bSortKey > aSortKey) {
        return 1;
    }
    return 0;
}

/**
 * Used to sort an array of objects.
 *
 * @param {Order} order - The order to sort by.
 * @param {string} orderBy - The key/accessor to sort by.
 * @returns {Function} A comparator function that can be used to sort.
 */
function getComparator(order: Order, orderBy: string) {
    return order === "descending"
        ? (a: any, b: any) => descendingComparator(a, b, orderBy)
        : (a: any, b: any) => -descendingComparator(a, b, orderBy);
}

/**
 * Sort an array of items.
 *
 * @param {Array<any>} items - The items to sort.
 * @param {Sort} sort - The sort object that contains the order and the key to
 * sort by.
 * @returns {Array<any>} The sorted items.
 */
export function sortData<T>(items: Array<T>, sort: Sort): Array<T> {
    const sortedData = items
        .slice()
        .sort(getComparator(sort.order, sort.orderBy));
    return sortedData;
}

export const getEffectiveColumnsPerPage = (width: number): number => {
    if (width <= parseInt(pureXsMax)) {
        return 2;
    }

    if (width >= parseInt(pureSmMin) && width <= parseInt(pureSmMax)) {
        return 3;
    }

    if (width >= parseInt(pureMdMin) && width <= parseInt(pureMdMax)) {
        return 4;
    }

    if (width >= parseInt(pureLgMin)) {
        return 5;
    }

    return 6;
};
